import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, FolderCog, Copy, CheckCircle2, ArrowRight, Sparkles,
} from "lucide-react";
import { waeAPI, OP_LABEL, fmtDT } from "./waeApi";
import { EmptyState, Modal, Fld, Footer, cls } from "./uiKit";
import { NumberInput } from "@/components/ui/number-input";

const KIND_LABEL = {
  user: "User", role: "Role", job_title: "Job Title",
  department_head: "Department Head", capability: "Capability",
  requester_manager: "Requester's Manager",
};

export default function WAETemplates() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null);      // template being edited/created

  const load = () => waeAPI.templates(typeFilter ? { approval_type: typeFilter } : {}).then(setRows);
  useEffect(() => { load(); }, [typeFilter]);   // eslint-disable-line
  useEffect(() => { waeAPI.approvalTypes().then(setTypes); }, []);

  const remove = async (id) => {
    if (!window.confirm("Deactivate this template? It will no longer be selected for new requests.")) return;
    try { await waeAPI.deleteTemplate(id); toast.success("Template deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const duplicate = (t) => {
    const copy = {
      ...t, id: undefined, _id: undefined,
      code: `${t.code}_copy_${Date.now().toString(36)}`,
      name: `${t.name} (Copy)`,
      is_default: false, version: undefined,
    };
    setEditing(copy);
  };

  return (
    <div className="space-y-4" data-testid="wae-templates">
      <div className="flex flex-wrap items-center gap-3">
        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
                data-testid="wae-templates-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All approval types</option>
          {types.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
        <button onClick={()=>setEditing({ __new: true })} data-testid="wae-templates-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Template
        </button>
      </div>

      {rows.length === 0
        ? <EmptyState icon={FolderCog} title="No templates yet"
            msg="Create a template to define who must approve what, when and in which order."/>
        : (
          <div className="space-y-3">
            {rows.map(t => (
              <div key={t.id} data-testid={`wae-template-${t.id}`}
                   className={`bg-white border rounded-2xl overflow-hidden ${t.is_active===false ? "opacity-60" : "border-slate-200"}`}>
                <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-hocp-navy text-white flex items-center justify-center shrink-0">
                    <FolderCog className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-[15px] font-semibold text-hocp-navy">{t.name}</div>
                      <span className="mono text-[11px] text-slate-400">{t.code}</span>
                      {t.is_default && <span className="px-2 py-0.5 rounded-md bg-hocp-gold/20 text-hocp-navy text-[10.5px] font-medium">DEFAULT</span>}
                      {t.is_active === false && <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10.5px] font-medium">INACTIVE</span>}
                      {t.auto_approve && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10.5px] font-medium">
                        <Sparkles className="w-3 h-3"/> Auto-Approve
                      </span>}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                      {t.approval_type_code} · v{t.version || 1} · updated {fmtDT(t.updated_at || t.created_at)}
                    </div>
                    {t.description && <div className="text-[12.5px] text-slate-600 mt-1.5">{t.description}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={()=>setEditing(t)} data-testid={`wae-template-edit-${t.id}`}
                            className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center">
                      <Pencil className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>duplicate(t)} data-testid={`wae-template-copy-${t.id}`}
                            className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center">
                      <Copy className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={()=>remove(t.id)} data-testid={`wae-template-delete-${t.id}`}
                            className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                </div>

                {/* Level chain preview */}
                <div className="p-4 flex items-center gap-2 overflow-x-auto">
                  {(t.levels || []).map((lv, i) => (
                    <div key={lv.key} className="flex items-center gap-2">
                      <div className="min-w-[160px] p-3 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Level {i + 1}</div>
                        <div className="font-medium text-[13px] text-hocp-navy mt-0.5 truncate">{lv.name}</div>
                        <div className="text-[10.5px] text-slate-500 mt-1">
                          {(lv.approvers || []).map(a =>
                            `${KIND_LABEL[a.kind]}${a.ref ? `: ${a.ref}` : ""}`).join(", ") || "—"}
                        </div>
                        {(lv.when || []).length > 0 && (
                          <div className="text-[10px] mt-1 text-violet-600">
                            when {lv.when.map(c => `${c.field} ${OP_LABEL[c.op]} ${c.value}`).join(" AND ")}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-1">SLA {lv.sla_hours || 24}h</div>
                      </div>
                      {i < t.levels.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0"/>}
                    </div>
                  ))}
                  <div className="min-w-[100px] p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto"/>
                    <div className="text-[11px] font-medium text-emerald-800 mt-1">Approved</div>
                  </div>
                </div>

                {t.auto_approve && (
                  <div className="px-4 pb-3 text-[11.5px] text-emerald-700">
                    <span className="font-medium">Auto-approve rule:</span>{" "}
                    {(t.auto_approve.when || []).map(c => `${c.field} ${OP_LABEL[c.op]} ${c.value}`).join(" AND ") || "always"}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      {editing && <TemplateEditor tpl={editing} approvalTypes={types}
                                    onClose={()=>setEditing(null)}
                                    onDone={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

// ─────────── Template Editor (form-based Workflow Designer) ───────────
function TemplateEditor({ tpl, approvalTypes, onClose, onDone }) {
  const isNew = tpl.__new || !tpl.id;
  const [f, setF] = useState(() => ({
    code: tpl.code || "",
    name: tpl.name || "",
    approval_type_code: tpl.approval_type_code || approvalTypes[0]?.code || "",
    description: tpl.description || "",
    is_default: !!tpl.is_default,
    is_active: tpl.is_active !== false,
    when: tpl.when || [],
    levels: tpl.levels?.length ? tpl.levels : [{
      key: `L${Date.now().toString(36)}`, name: "Manager",
      mode: "sequential", quorum: "any",
      approvers: [{ kind: "role", ref: "" }],
      sla_hours: 24, when: [],
    }],
    auto_approve: tpl.auto_approve || null,
  }));
  const [saving, setSaving] = useState(false);

  const setLevel = (idx, patch) => {
    const next = [...f.levels]; next[idx] = { ...next[idx], ...patch }; setF({ ...f, levels: next });
  };
  const addLevel = () => setF({
    ...f, levels: [...f.levels, {
      key: `L${Date.now().toString(36)}`, name: `Level ${f.levels.length + 1}`,
      mode: "sequential", quorum: "any",
      approvers: [{ kind: "role", ref: "" }], sla_hours: 24, when: [],
    }],
  });
  const removeLevel = (i) => setF({ ...f, levels: f.levels.filter((_, idx) => idx !== i) });
  const moveLevel = (i, dir) => {
    const to = i + dir; if (to < 0 || to >= f.levels.length) return;
    const next = [...f.levels]; [next[i], next[to]] = [next[to], next[i]]; setF({ ...f, levels: next });
  };

  const submit = async () => {
    if (!f.code.match(/^[a-z0-9_.-]+$/)) { toast.error("Code must be lowercase, no spaces"); return; }
    if (!f.name.trim() || !f.approval_type_code) { toast.error("Name and approval type required"); return; }
    if (!f.levels.length) { toast.error("At least one level required"); return; }
    setSaving(true);
    try {
      if (isNew) await waeAPI.createTemplate(f);
      else       await waeAPI.updateTemplate(tpl.id, {
        name: f.name, description: f.description,
        is_default: f.is_default, is_active: f.is_active,
        when: f.when, levels: f.levels, auto_approve: f.auto_approve,
      });
      toast.success(isNew ? "Template created" : "Template updated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "New Workflow Template" : `Edit · ${tpl.name}`} onClose={onClose} size="4xl">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Code" hint="Unique identifier, e.g. refund_high_value">
            <input value={f.code} onChange={e=>setF({...f, code: e.target.value})}
                   disabled={!isNew} data-testid="wae-tpl-code" className={cls}/>
          </Fld>
          <Fld label="Name">
            <input value={f.name} onChange={e=>setF({...f, name: e.target.value})}
                   data-testid="wae-tpl-name" className={cls}/>
          </Fld>
          <Fld label="Approval Type">
            <select value={f.approval_type_code} onChange={e=>setF({...f, approval_type_code: e.target.value})}
                    disabled={!isNew} data-testid="wae-tpl-type" className={cls}>
              {approvalTypes.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
            </select>
          </Fld>
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={f.is_default} onChange={e=>setF({...f, is_default: e.target.checked})}
                     data-testid="wae-tpl-default"/> Default template for this type
            </label>
            <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={f.is_active} onChange={e=>setF({...f, is_active: e.target.checked})}
                     data-testid="wae-tpl-active"/> Active
            </label>
          </div>
          <Fld label="Description" full>
            <input value={f.description} onChange={e=>setF({...f, description: e.target.value})}
                   data-testid="wae-tpl-desc" className={cls}/>
          </Fld>
        </div>

        {/* Template selection condition */}
        <ConditionEditor
          title="Applies When (all-must-match)"
          hint="Optional. Leave empty to make this the default template."
          conditions={f.when}
          onChange={when => setF({...f, when})}
          testid="wae-tpl-when"/>

        {/* Levels */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-display text-[14px] font-semibold text-hocp-navy">Approval Levels</div>
            <button onClick={addLevel} data-testid="wae-tpl-level-add"
                    className="h-8 px-3 rounded-lg border border-dashed border-slate-300 text-[12px] hover:bg-slate-50 inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5"/> Add Level
            </button>
          </div>

          {f.levels.map((lv, i) => (
            <LevelEditor key={lv.key} idx={i} level={lv}
              onChange={(patch)=>setLevel(i, patch)}
              onRemove={()=>removeLevel(i)}
              onUp={()=>moveLevel(i, -1)}
              onDown={()=>moveLevel(i, 1)}
              canRemove={f.levels.length > 1}
              canUp={i > 0} canDown={i < f.levels.length - 1}
              testid={`wae-tpl-level-${i}`}/>
          ))}
        </div>

        {/* Auto-approve rule */}
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={!!f.auto_approve}
                   onChange={e=>setF({...f, auto_approve: e.target.checked ? { when: [], reason: "Auto-approved by rule" } : null})}
                   data-testid="wae-tpl-auto-toggle"/>
            <span className="text-[13px] font-medium text-emerald-800 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5"/> Auto-Approval Rule
            </span>
          </div>
          {f.auto_approve && (
            <>
              <ConditionEditor
                title="Auto-approve when"
                conditions={f.auto_approve.when || []}
                onChange={when => setF({...f, auto_approve: {...f.auto_approve, when}})}
                testid="wae-tpl-auto-when"/>
              <Fld label="Auto-approval note (audit)">
                <input value={f.auto_approve.reason || ""}
                       onChange={e=>setF({...f, auto_approve: {...f.auto_approve, reason: e.target.value}})}
                       data-testid="wae-tpl-auto-reason" className={cls}/>
              </Fld>
            </>
          )}
        </div>
      </div>

      <Footer onClose={onClose} onSubmit={submit} saving={saving}
              submitLabel={isNew ? "Create Template" : "Save Changes"} testid="wae-tpl-save"/>
    </Modal>
  );
}

function LevelEditor({ idx, level, onChange, onRemove, onUp, onDown, canRemove, canUp, canDown, testid }) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-hocp-navy text-white text-[12px] font-semibold flex items-center justify-center">{idx + 1}</div>
        <input value={level.name} onChange={e=>onChange({name: e.target.value})}
               data-testid={`${testid}-name`}
               className="flex-1 h-8 px-2 rounded-lg border border-slate-200 text-[13px] font-medium focus:border-hocp-navy outline-none"/>
        <button onClick={onUp} disabled={!canUp} className="h-8 w-8 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid={`${testid}-up`}>↑</button>
        <button onClick={onDown} disabled={!canDown} className="h-8 w-8 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid={`${testid}-down`}>↓</button>
        <button onClick={onRemove} disabled={!canRemove} className="h-8 w-8 rounded hover:bg-rose-50 text-rose-500 disabled:opacity-30" data-testid={`${testid}-remove`}>
          <Trash2 className="w-3.5 h-3.5"/>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Fld label="Mode">
          <select value={level.mode} onChange={e=>onChange({mode: e.target.value})} data-testid={`${testid}-mode`} className={cls}>
            <option value="sequential">Sequential</option>
            <option value="parallel">Parallel</option>
          </select>
        </Fld>
        <Fld label="Quorum">
          <select value={level.quorum} onChange={e=>onChange({quorum: e.target.value})} data-testid={`${testid}-quorum`} className={cls}>
            <option value="any">Any 1 approves</option>
            <option value="all">All must approve</option>
          </select>
        </Fld>
        <Fld label="SLA (hours)">
          <NumberInput min="1" value={level.sla_hours || 24}
                 onChange={e=>onChange({sla_hours: parseInt(e.target.value) || 24})}
                 data-testid={`${testid}-sla`} className={cls}/>
        </Fld>
      </div>

      {/* Approvers */}
      <div className="mt-3">
        <div className="text-[11.5px] text-slate-500 mb-1.5 font-medium">Approvers</div>
        <div className="space-y-2">
          {(level.approvers || []).map((a, ai) => (
            <div key={ai} className="grid grid-cols-12 gap-2" data-testid={`${testid}-approver-${ai}`}>
              <select value={a.kind}
                      onChange={e=>{
                        const next = [...level.approvers];
                        next[ai] = { ...next[ai], kind: e.target.value };
                        onChange({approvers: next});
                      }}
                      data-testid={`${testid}-approver-kind-${ai}`} className={cls + " col-span-4"}>
                {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {!["department_head","requester_manager"].includes(a.kind) && (
                <input value={a.ref || ""} placeholder="ref (role/user/capability id)" aria-label="Approver reference (role/user/capability id)"
                       onChange={e=>{
                         const next = [...level.approvers];
                         next[ai] = { ...next[ai], ref: e.target.value };
                         onChange({approvers: next});
                       }}
                       data-testid={`${testid}-approver-ref-${ai}`} className={cls + " col-span-7"}/>
              )}
              {["department_head","requester_manager"].includes(a.kind) && (
                <div className="col-span-7 text-[12px] text-slate-500 self-center pl-2 italic">
                  Auto-resolved from requester&apos;s Workforce OS profile.
                </div>
              )}
              <button onClick={()=>onChange({approvers: (level.approvers||[]).filter((_,x)=>x!==ai)})}
                      disabled={(level.approvers||[]).length <= 1}
                      data-testid={`${testid}-approver-remove-${ai}`}
                      className="col-span-1 h-10 rounded-lg hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center disabled:opacity-30">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
        </div>
        <button onClick={()=>onChange({approvers: [...(level.approvers||[]), {kind:"role", ref:""}]})}
                data-testid={`${testid}-approver-add`}
                className="mt-2 h-8 px-3 rounded-lg border border-dashed border-slate-300 text-[12px] hover:bg-slate-50 inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5"/> Add Approver
        </button>
      </div>

      <ConditionEditor
        title="Skip this level unless (optional)"
        conditions={level.when || []}
        onChange={when => onChange({when})}
        testid={`${testid}-when`}/>
    </div>
  );
}

function ConditionEditor({ title, hint, conditions, onChange, testid }) {
  const OPS = Object.entries(OP_LABEL);
  const addRow = () => onChange([...(conditions||[]), { field: "amount", op: "gt", value: 0 }]);
  const removeRow = (i) => onChange((conditions||[]).filter((_, x) => x !== i));
  const setRow = (i, patch) => {
    const next = [...(conditions||[])]; next[i] = { ...next[i], ...patch }; onChange(next);
  };
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="text-[11.5px] text-slate-500 font-medium">{title}</div>
      {hint && <div className="text-[10.5px] text-slate-400 mb-1">{hint}</div>}
      {(conditions||[]).map((c, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mt-1" data-testid={`${testid}-row-${i}`}>
          <input value={c.field} onChange={e=>setRow(i, {field: e.target.value})}
                 placeholder="field (e.g. amount)" data-testid={`${testid}-field-${i}`} className={cls + " col-span-5"}/>
          <select value={c.op} onChange={e=>setRow(i, {op: e.target.value})}
                  data-testid={`${testid}-op-${i}`} className={cls + " col-span-3"}>
            {OPS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input value={String(c.value ?? "")}
                 onChange={e=>{
                   const v = e.target.value;
                   setRow(i, {value: /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v});
                 }}
                 placeholder="value" data-testid={`${testid}-value-${i}`} className={cls + " col-span-3"}/>
          <button onClick={()=>removeRow(i)} data-testid={`${testid}-remove-${i}`}
                  className="col-span-1 h-10 rounded-lg hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center">
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        </div>
      ))}
      <button onClick={addRow} data-testid={`${testid}-add`}
              className="mt-2 h-7 px-2.5 rounded-lg border border-dashed border-slate-300 text-[11.5px] hover:bg-slate-50 inline-flex items-center gap-1.5">
        <Plus className="w-3 h-3"/> Add condition
      </button>
    </div>
  );
}
